import { NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, NgZone, ChangeDetectorRef, OnInit } from '@angular/core';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import {
  Menu,
  MenuGroupItem,
  MenuItem,
  MenuPanelItem
} from '../../../domain/menu.domain';
import { MenuService } from '../../../services/menu/menu.service';
import { IfFeatureService } from '../../../services/if-feature/if-feature.service';
import { IconComponent } from '../../../components/icon/icon.component';
import { BadgeComponent } from '../../../components/badge/badge.component';
import { MenuItemContentComponent } from '../item-content/item-content.component';
import { MenuItemToggleComponent } from '../item-toggle/item-toggle.component';
import { MenuItemComponent } from '../item/item.component';
import { MenuNavigationBaseComponent } from '../menu-navigation-base.component';
import { MenuSearchNavigationComponent } from '../search-navigation/search-navigation.component';
import { MenuToolbarComponent } from './toolbar/menu-toolbar.component';

/**
 * The Menu Local Navigation Component
 *
 * This is the local navigation for menu
 */
@Component({
  selector: 'td-wb-menu-local-navigation',
  templateUrl: 'local-navigation.component.html',
  styleUrls: ['local-navigation.component.scss'],
  imports: [
    TranslatePipe,
    MenuItemComponent,
    IconComponent,
    MenuItemToggleComponent,
    MenuItemContentComponent,
    NgStyle,
    MenuSearchNavigationComponent,
    MenuToolbarComponent,
    TranslateDirective,
    BadgeComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MenuLocalNavigationComponent extends MenuNavigationBaseComponent implements OnInit {
  private static readonly TRADING: string = 'trading';
  private static readonly BUYSELL: string = 'Buy/Sell';
  private static readonly BUYSELL_FR: string = 'Achat/vente';
  private static readonly FRACTIONAL_LINK: string = '#/page/fractional-landing';
  protected logoUrl: string;
  protected logoLabelTranslationId: string;

  @Input() navigationMenu: Menu;

  @Input() activeItemId: string;

  private readonly disableFractionalTrading: boolean = false;

  /**
   * The constructor of the component.
   *
   * @param menuService The menu service
   */
  constructor(private menuService: MenuService,
              private featureService: IfFeatureService,
              changeDetectorRef: ChangeDetectorRef,
              ngZone: NgZone) {
    super(ngZone, changeDetectorRef);
    this.disableFractionalTrading = this.featureService.getFeatureFlag<boolean>('disableFractionalTrading');
    this.logoUrl = this.menuService.getLogoUrl();
    this.logoLabelTranslationId = this.menuService.getLogoLabelTranslationId();
  }

  ngOnInit(): void {
    if (this.disableFractionalTrading) {
      const trading: Array<MenuPanelItem> = this.navigationMenu.localNavigation.filter((item: MenuPanelItem) =>
        item.id === MenuLocalNavigationComponent.TRADING);
      const buySell: Array<MenuGroupItem & MenuItem> = trading[0].menu[0].item.filter((item: MenuItem) =>
        item.text === MenuLocalNavigationComponent.BUYSELL || item.text === MenuLocalNavigationComponent.BUYSELL_FR);
      const buySellLinks = buySell[0].menu[0].item;
      const fractionalLinkIndex = buySellLinks.indexOf(buySellLinks.filter((item: MenuItem) =>
        item.href === MenuLocalNavigationComponent.FRACTIONAL_LINK)[0]);
      if (fractionalLinkIndex !== -1) {
        buySellLinks.splice(fractionalLinkIndex, 1);
      }
    }
  }

  getActiveItemId(): string {
    return this.menuService.getCurrentLocalNavRootMenuId();
  }

  hasFlyOut(item: MenuPanelItem): boolean {
    return (
      item !== undefined &&
      item.menu !== undefined &&
      (item.menu[0].item !== undefined || item.menu.length > 1)
    );
  }

  showSecondaryContentColumn(itemMenu: Array<MenuGroupItem | MenuItem>, itemMenuGroup: MenuGroupItem & MenuItem): boolean {
    return itemMenu.length === 1 && (itemMenuGroup.href !== undefined || itemMenuGroup.menu[0].item.length > 0);
  }

  trackByIndex(index: number): number {
    return index;
  }

  isExternalLink(item: MenuItem): boolean {
    return item.href !== undefined && (!!item.target  || !!item.external);
  }

  protected showNewBadge(value: string): boolean {
    return this.menuService.showNewBadge(value);
  }
}
